"use client";
import React, { HTMLAttributes, useEffect, useState } from "react";
import * as RadixDialog from "@radix-ui/react-dialog";
import cn from "classnames";
import Icon from "./icon";
import Button from "./button";

export interface ModalProps extends RadixDialog.DialogProps {
  trigger?: React.ReactElement<typeof Trigger>;
  dark?: boolean;
}

function Modal(props: ModalProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Clean up pointer-events when modal closes
  useEffect(() => {
    if (!mounted || props.open !== false) return;

    // Small delay to ensure Radix cleanup happens first
    const cleanup = () => {
      const body = document.body;
      if (body.style.pointerEvents === "none") {
        body.style.removeProperty("pointer-events");
      }
    };

    setTimeout(cleanup, 50);
    // Also run cleanup immediately in case there's no delay needed
    cleanup();
  }, [mounted, props.open]);

  return (
    <>
      <RadixDialog.Root {...props}>
        {props.trigger ? (
          <RadixDialog.Trigger asChild>{props.trigger}</RadixDialog.Trigger>
        ) : undefined}
        <RadixDialog.Portal container={mounted ? document.body : undefined}>
          {/* ROOT */}
          <div className="z-50 fixed inset-0">
            <RadixDialog.Overlay className="-z-[1] flex justify-center items-center bg-black/25 backdrop-blur-sm h-full data-[state=open]:animate-overlayShow">
              {/* SCROLLABLE */}
              <div className="flex justify-center items-center w-full h-full">
                <RadixDialog.Content
                  className={`z-50 m-8 ${
                    props.dark ? "bg-secondary-100" : "bg-surface"
                  } overflow-x-hidden rounded-xl relative shadow-elevation-400 focus:outline-none max-h-[calc(100%_-_64px)] overflow-y-auto`}
                >
                  {props.children}
                </RadixDialog.Content>
              </div>
            </RadixDialog.Overlay>
          </div>
        </RadixDialog.Portal>
      </RadixDialog.Root>
    </>
  );
}

function Trigger(props: RadixDialog.DialogTriggerProps) {
  return <RadixDialog.Trigger {...props}>{props.children}</RadixDialog.Trigger>;
}

interface ModalHeaderProps {
  title?: string | JSX.Element;
  separator?: boolean;
  showUser?: boolean;
  onClose?: () => void;
}
function Header(props: ModalHeaderProps) {
  return (
    <>
      <div className="flex flex-row justify-between items-center gap-4 p-6">
        <div className="flex justify-center items-center">
          {props.title ? (
            <>
              <RadixDialog.Title className="text-headline-bold uppercase">
                {props.title}
              </RadixDialog.Title>
              {props.showUser && <></>}
            </>
          ) : undefined}
        </div>
        <RadixDialog.Close
          asChild
          onClick={() => props.onClose && props.onClose()}
        >
          <Button variant="circle" color="tertiary">
            <Icon name="close" size="sm" />
          </Button>
        </RadixDialog.Close>
      </div>
      {props.separator && <hr className="border-surface-600" />}
    </>
  );
}

function Footer(props: HTMLAttributes<HTMLDivElement>) {
  return (
    <>
      <hr />
      <div
        {...props}
        className={cn(
          `flex justify-between items-center px-6 pt-2 pb-6`,
          props.className
        )}
      >
        <RadixDialog.Close asChild>
          <Button ghost color="tertiary">
            Back
          </Button>
        </RadixDialog.Close>
      </div>
    </>
  );
}

Modal.Trigger = Trigger;
Modal.Header = Header;
Modal.Footer = Footer;
export default Modal;
